import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import {
  STORE_VISIBILITY_VALUES,
  accountStatusLabelFromRow,
  accountStatusLabelToBoolean,
  getStoreVisibilityLabel,
  mapKitchenOperationUpdateErrorsToForm,
} from '../../context-api/api-services/StoreManagement.service';
import { useStoreManagementData } from '../../context-api/store-management/StoreManagementDataContext';
import { getManagingBranchNameForApi } from '../../utils/managingBranchName';
import {
  storeManagementMenuPaperSx as menuPaperSx,
  storeManagementMenuItemSx as menuItemSx,
  storeManagementOutlinedSelectSx as outlinedSelectSx,
} from '../../utils/storeManagementSelectSx';
import { ACCOUNT_STATUS_OPTIONS, COLORS } from './storeManagementConstants';
import {
  KitchenAvatar,
  getStatusDisplay,
  getStoreVisibilityDisplay,
} from './storeManagementParts';

function normalizeVisibilityForForm(canonical) {
  if (canonical == null || canonical === '') return '';
  return String(canonical).trim().toLowerCase();
}

const VISIBILITY_WHEN_ACTIVE = STORE_VISIBILITY_VALUES;
const VISIBILITY_WHEN_INACTIVE = ['under_review', 'under_testing'];

function visibilityOptionsForAccountStatus(accountStatusLabel) {
  return accountStatusLabel === 'Active' ? VISIBILITY_WHEN_ACTIVE : VISIBILITY_WHEN_INACTIVE;
}

function coerceVisibilityForAccountStatus(visibility, accountStatusLabel) {
  const allowed = visibilityOptionsForAccountStatus(accountStatusLabel);
  if (visibility && allowed.includes(visibility)) return visibility;
  return '';
}

function productCountFromRow(row) {
  if (!row) return 0;
  if (typeof row.products === 'number' && Number.isFinite(row.products)) {
    return Math.max(0, row.products);
  }
  const raw = row.rawData?.total_products;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

const NO_PRODUCTS_ERROR = {
  errorMessage:
    'This kitchen has no products. Status cannot be updated until at least one product is added.',
  param: 'total_products',
};

const VISIBILITY_REQUIRED_ERROR = {
  errorMessage: 'Store visibility is required.',
  param: 'store_visibility_status',
};

export function StoreManagementUpdateStatusModal({ open, row, onClose, onSuccess }) {
  const { regionalUpdateKitchenOperation, updateKitchenOperationData } = useStoreManagementData();
  const loading = updateKitchenOperationData.loading;

  const [accountStatus, setAccountStatus] = useState('Active');
  const [storeVisibility, setStoreVisibility] = useState('');
  const [formErrors, setFormErrors] = useState([]);

  const baselineAccount = useMemo(
    () => (row ? accountStatusLabelFromRow(row) : 'Active'),
    [row]
  );
  const baselineVisibility = useMemo(
    () => (row ? normalizeVisibilityForForm(row.storeVisibilityStatus) : ''),
    [row]
  );

  const visibilityOptions = useMemo(
    () => visibilityOptionsForAccountStatus(accountStatus),
    [accountStatus]
  );

  const productCount = useMemo(() => productCountFromRow(row), [row]);
  const hasNoProducts = productCount === 0;
  const visibilityMissing = storeVisibility === '';
  const visibilityFieldError = formErrors.some(
    (err) => err.param === 'store_visibility_status'
  );

  useEffect(() => {
    if (!open || !row) return;
    setAccountStatus(baselineAccount);
    setStoreVisibility(coerceVisibilityForAccountStatus(baselineVisibility, baselineAccount));
    setFormErrors(hasNoProducts ? [NO_PRODUCTS_ERROR] : []);
  }, [open, row, baselineAccount, baselineVisibility, hasNoProducts]);

  const handleAccountStatusChange = (nextStatus) => {
    setAccountStatus(nextStatus);
    setStoreVisibility((prev) => coerceVisibilityForAccountStatus(prev, nextStatus));
  };

  const unchanged =
    accountStatus === baselineAccount && storeVisibility === baselineVisibility;

  const handleClose = () => {
    if (loading) return;
    onClose?.();
  };

  const handleSave = async () => {
    if (!row || unchanged || loading || hasNoProducts) return;
    setFormErrors([]);

    if (productCountFromRow(row) === 0) {
      setFormErrors([NO_PRODUCTS_ERROR]);
      return;
    }

    if (!storeVisibility) {
      setFormErrors([VISIBILITY_REQUIRED_ERROR]);
      return;
    }

    const kitchenName = String(row.rawData?.kitchenName ?? row.kitchenName ?? '').trim();
    if (!kitchenName) {
      setFormErrors([{ errorMessage: 'Kitchen name is missing.', param: 'kitchen_name' }]);
      return;
    }

    const managing = getManagingBranchNameForApi();
    if (managing == null || String(managing).trim() === '') {
      setFormErrors([{ errorMessage: 'Managing branch is not set.', param: 'managing_branch_name' }]);
      return;
    }

    const payload = {
      kitchen_name: kitchenName,
      managing_branch_name: String(managing).trim().toLowerCase().slice(0, 50),
    };

    if (accountStatus !== baselineAccount) {
      payload.status = accountStatusLabelToBoolean(accountStatus);
    }
    if (storeVisibility !== baselineVisibility) {
      payload.store_visibility_status = storeVisibility;
    }

    if (payload.status === undefined && payload.store_visibility_status === undefined) {
      return;
    }

    const res = await regionalUpdateKitchenOperation(payload);
    if (res?.hasError) {
      setFormErrors([
        { errorMessage: res.message || 'Failed to update store status.', param: 'status_update' },
      ]);
      return;
    }

    const body = res?.data ?? {};
    if (body.success === true) {
      onSuccess?.();
      onClose?.();
      return;
    }

    setFormErrors(mapKitchenOperationUpdateErrorsToForm(body));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '20px',
          overflow: 'hidden',
          border: `1px solid ${COLORS.border}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 32px rgba(63,63,110,0.10)',
          bgcolor: COLORS.white,
          maxWidth: 480,
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          pt: { xs: 2.5, sm: 3 },
          pb: 2,
          bgcolor: COLORS.white,
          borderBottom: `1px solid ${COLORS.border}`,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={handleClose}
          disabled={loading}
          size="small"
          aria-label="Close"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: COLORS.muted,
            '&:hover': { bgcolor: 'rgba(63, 63, 110, 0.06)', color: COLORS.ink },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, pr: 4 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              bgcolor: COLORS.accentOrange,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 4px 14px rgba(255, 123, 41, 0.35)',
            }}
          >
            <SyncAltOutlinedIcon sx={{ fontSize: 22, color: COLORS.white }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: 'Inter Bold, sans-serif',
                fontSize: { xs: 18, sm: 20 },
                fontWeight: 700,
                color: COLORS.ink,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              Update status
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Inter Regular, sans-serif',
                fontSize: 13,
                color: COLORS.muted,
                mt: 0.35,
              }}
            >
              Change account or store visibility
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2.5,
          bgcolor: '#fafbfc',
        }}
      >
        {row && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: 1.75,
                mb: 2,
                borderRadius: '12px',
                bgcolor: COLORS.white,
                border: `1px solid ${COLORS.border}`,
              }}
            >
              <KitchenAvatar name={row.kitchenName} size={40} radius="10px" />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter Bold, sans-serif',
                    fontSize: 14,
                    fontWeight: 700,
                    color: COLORS.ink,
                    lineHeight: 1.3,
                  }}
                >
                  {row.kitchenName}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                  {getStatusDisplay(row.status)}
                  {getStoreVisibilityDisplay(row.storeVisibilityStatus)}
                </Box>
              </Box>
            </Box>

            {formErrors.length > 0 && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: '8px',
                  bgcolor: '#fee2e2',
                  border: '1px solid #fca5a5',
                }}
              >
                {formErrors.map((err, i) => (
                  <Typography
                    key={i}
                    sx={{
                      fontFamily: 'Inter Regular, sans-serif',
                      fontSize: 13,
                      color: '#dc2626',
                      lineHeight: 1.45,
                    }}
                  >
                    {err.errorMessage}
                  </Typography>
                ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth size="small" sx={outlinedSelectSx}>
                <InputLabel id="store-update-account-status-label">Account status</InputLabel>
                <Select
                  labelId="store-update-account-status-label"
                  label="Account status"
                  value={accountStatus}
                  onChange={(e) => handleAccountStatusChange(e.target.value)}
                  disabled={loading || hasNoProducts}
                  MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                >
                  {ACCOUNT_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt} sx={menuItemSx}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                fullWidth
                size="small"
                required
                error={visibilityFieldError}
                sx={outlinedSelectSx}
              >
                <InputLabel id="store-update-visibility-label" shrink>
                  Store visibility
                </InputLabel>
                <Select
                  labelId="store-update-visibility-label"
                  label="Store visibility"
                  value={storeVisibility}
                  onChange={(e) => {
                    setStoreVisibility(e.target.value);
                    setFormErrors((prev) =>
                      prev.filter((err) => err.param !== 'store_visibility_status')
                    );
                  }}
                  disabled={loading || hasNoProducts}
                  displayEmpty
                  notched
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography
                          component="span"
                          sx={{ color: COLORS.muted, fontSize: 13 }}
                        >
                          Select visibility
                        </Typography>
                      );
                    }
                    return getStoreVisibilityLabel(selected);
                  }}
                  MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                >
                  {visibilityOptions.map((v) => (
                    <MenuItem key={v} value={v} sx={menuItemSx}>
                      {getStoreVisibilityLabel(v)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column-reverse', sm: 'row' },
                gap: 1.25,
                mt: 2.5,
              }}
            >
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClose}
                disabled={loading}
                sx={{
                  height: 42,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontFamily: 'Inter Regular, sans-serif',
                  fontSize: 14,
                  fontWeight: 600,
                  color: COLORS.ink,
                  borderColor: COLORS.border,
                  '&:hover': {
                    borderColor: COLORS.ink,
                    bgcolor: 'rgba(63, 63, 110, 0.04)',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSave}
                disabled={loading || unchanged || hasNoProducts || visibilityMissing}
                sx={{
                  height: 42,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontFamily: 'Inter Bold, sans-serif',
                  fontSize: 14,
                  fontWeight: 700,
                  bgcolor: COLORS.accentOrange,
                  boxShadow: '0 3px 10px rgba(255, 123, 41, 0.30)',
                  '&:hover': { bgcolor: COLORS.labelFocusOrange },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(255, 123, 41, 0.45)',
                    color: 'rgba(255,255,255,0.9)',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={20} sx={{ color: COLORS.white }} />
                ) : (
                  'Save changes'
                )}
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
